import { 
  Terminal, 
  BookOpen, 
  FolderPlus, 
  Wrench, 
  Send, 
  Award, 
  PencilLine, 
  Link, 
  CircleX 
} from 'lucide-react'
import { TelemetryEvent } from './types'

interface LogLineRowProps {
  event: TelemetryEvent
}

export function LogLineRow({ event }: LogLineRowProps) {
  // Parse timestamp
  const dateObj = new Date(event.timestamp)
  const timeStr = dateObj.toTimeString().split(' ')[0] // e.g. "14:31:20"

  // Parse Metadata & Agent info
  let parsedMetadata: any = {}
  try {
    if (event.metadata) parsedMetadata = JSON.parse(event.metadata)
  } catch {}

  let parsedAgent: any = {}
  try {
    if (event.agent) parsedAgent = JSON.parse(event.agent)
  } catch {}

  // Determine Agent label
  let agentLabel = 'Human'
  if (parsedAgent.type) {
    if (parsedAgent.type === 'antigravity') agentLabel = 'antigravity'
    else if (parsedAgent.type === 'cursor') agentLabel = 'Cursor'
    else if (parsedAgent.type === 'claude_code') agentLabel = 'Claude Code'
    else agentLabel = parsedAgent.type
  }

  // Determine action info
  let IconComponent = BookOpen
  let iconColor = 'text-gray-400'
  let summary = 'unknown'
  let isError = false
  let errorMsg = ''

  if (event.event_type === 'cli_command') {
    const cmd = parsedMetadata.command || 'cli'
    if (cmd === 'init') {
      IconComponent = FolderPlus
      iconColor = 'text-amber-500'
      summary = 'init'
    } else if (cmd === 'connect') {
      IconComponent = FolderPlus
      iconColor = 'text-indigo-400'
      summary = 'connect'
    } else if (cmd === 'build') {
      IconComponent = Wrench
      iconColor = 'text-[#38bdf8]'
      summary = 'build'
    } else if (cmd === 'push') {
      IconComponent = Send
      iconColor = 'text-emerald-400'
      summary = 'push'
    } else if (cmd === 'examples') {
      IconComponent = BookOpen
      iconColor = 'text-violet-400'
      const exampleSlug = parsedMetadata.exampleSlug || parsedMetadata.args?.[0]
      summary = exampleSlug ? `examples ${exampleSlug}` : 'examples'
    } else {
      IconComponent = Wrench
      iconColor = 'text-[#38bdf8]'
      summary = cmd
    }
  } else if (event.event_type === 'docs_access') {
    IconComponent = BookOpen
    iconColor = 'text-indigo-400'
    summary = `/${parsedMetadata.docName || 'README.md'}`
  } else if (event.event_type === 'skill_usage') {
    IconComponent = Award
    iconColor = 'text-yellow-400'
    summary = `skill: ${parsedMetadata.skillName || 'unknown'}`
  } else if (event.event_type === 'game_registered') {
    IconComponent = PencilLine
    iconColor = 'text-pink-400'
    summary = `registered`
  } else if (event.event_type === 'invitation_accessed') {
    const context = parsedMetadata.context || 'accessed'
    if (context === 'raw_markdown') {
      IconComponent = Link
      iconColor = 'text-cyan-400'
      summary = `invite: raw_markdown`
    } else if (context === 'api_claim') {
      IconComponent = FolderPlus
      iconColor = 'text-emerald-400'
      summary = `invite: api-claim`
    } else if (context === 'api_fetch') {
      IconComponent = FolderPlus
      iconColor = 'text-blue-400'
      summary = `invite: api-fetch`
    } else {
      IconComponent = FolderPlus
      iconColor = 'text-gray-400'
      summary = `invite: ${context}`
    }
  } else if (event.event_type === 'game_crash') {
    IconComponent = CircleX
    iconColor = 'text-red-500'
    const count = (event as any).count || 1
    summary = count > 1 ? `errors (x${count})` : 'errors'
    isError = true
    errorMsg = parsedMetadata.error || 'render crash'
  }

  // Check for error in metadata
  if (parsedMetadata.error || parsedMetadata.success === false) {
    isError = true
    errorMsg = parsedMetadata.error || 'Failed'
  }

  return (
    <div 
      className={`flex h-7 items-center justify-between px-3 rounded font-mono text-[11px] leading-none border select-none transition-colors ${
        isError 
          ? 'bg-red-950/10 border-red-900/30 text-red-400 hover:bg-red-950/20' 
          : 'bg-[#0f1016] border-[#181a25] text-gray-300 hover:bg-[#151722]'
      }`}
    >
      <div className="flex items-center space-x-3 truncate">
        {/* Action Icon */}
        <IconComponent className={`h-3.5 w-3.5 flex-shrink-0 ${isError ? 'text-red-400' : iconColor}`} />
        
        {/* Time of Day */}
        <span className="text-[10px] text-muted-foreground select-none flex-shrink-0">{timeStr}</span>
        
        {/* Summary Description */}
        <span className="font-semibold truncate max-w-[300px]">{summary}</span>
        
        {/* Error message if present */}
        {isError && (
          <span className="text-[10px] text-red-500 font-bold uppercase tracking-tight truncate flex-shrink-0">
            [Err: {errorMsg}]
          </span>
        )}
      </div>

      {/* Agent details */}
      <div className="flex-shrink-0 text-[10px] text-muted-foreground flex items-center space-x-1 select-none">
        <Terminal className="h-3 w-3 opacity-60" />
        <span>{agentLabel}</span>
      </div>
    </div>
  )
}
